
import { useState, useEffect } from 'react';
import { X, Check, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { areCookiesEnabled } from '@/utils/cookies';
import { useToast } from '@/hooks/use-toast';

export const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  const { toast } = useToast();
  
  // Check if we need to show the consent dialog
  useEffect(() => {
    // Don't show if user has already consented
    const hasConsented = localStorage.getItem('cookie-consent') === 'true';
    
    if (!hasConsented) {
      // Check if cookies are enabled
      const cookiesEnabled = areCookiesEnabled();
      setShowConsent(!cookiesEnabled || !hasConsented);
      
      if (!cookiesEnabled) {
        console.log('Cookies are disabled or blocked by the browser');
      }
    }
  }, []);
  
  const handleAccept = () => {
    try {
      localStorage.setItem('cookie-consent', 'true');
      setShowConsent(false);
      
      // Reload the page to ensure cookies are loaded correctly
      window.location.reload();
      
      toast({
        title: "Cookies enabled",
        description: "Your fasting data will now be saved between sessions.",
      });
    } catch (error) {
      console.error('Error saving consent:', error);
      
      toast({
        title: "Error enabling cookies",
        description: "Please check your browser settings and try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'false');
    setShowConsent(false);
    
    toast({
      title: "Cookies not enabled",
      description: "Your fasting data will not be saved between sessions.",
    });
  };
  
  if (!showConsent) return null;
  
  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-1">
          <Cookie className="h-6 w-6 text-fasttrack-blue" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium mb-1">Cookie permission</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            This app needs to use cookies to save your fasting progress. Without cookies, your fasting data will be lost when you reload the page.
          </p>
          <div className="flex gap-2 justify-end">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDecline}
              className="text-xs h-8"
            >
              <X className="h-3 w-3 mr-1" /> Decline
            </Button>
            <Button 
              size="sm" 
              onClick={handleAccept}
              className="text-xs bg-fasttrack-blue hover:bg-fasttrack-blue/90 h-8"
            >
              <Check className="h-3 w-3 mr-1" /> Allow cookies
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
